import React, { FC, useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import _sortBy from 'lodash/sortBy';
import moment from 'moment';
import { BasicContainer, BodyText, BodyTextSmall, FlexDynamic } from '@minecraft.atoms';
import MediaBin from '@minecraft.media-bin';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { MediaTypeCodeEnum, MediaAssetOnProfileFragment } from '@minecraft.graphql-operations';
import { gqlMediaTypeCodeEnum, gqlProfileMedia } from '@minecraft.graphql-types';
import Modal, { useModal } from '@minecraft.modal';
import Icon from '@minecraft.icon';
import MeatballMenu from '@minecraft.meatball-menu';
import DropdownItem from '@minecraft.dropdown-item';
import { SCREEN_NAMES, AppType } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { useBillingPopup, PurchaseResultType, PaywallLocation } from '@blocs.paywall';
import { getAgeInYears } from '@minecraft.date-utils';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import CloudinaryMediaViewer from '@minecraft.cloudinary-media-viewer';
import { MediaUploader } from '../../MediaUploader';
import { MediaUploaderProps } from '../../MediaUploader/types';
import CreditMedia from '../Credits/CreditMedia';
import { AnchorButton } from '../styles/ProfileView';
import { useProfileViewContext } from '../ProfileViewContext';
import { captureExceptionEvent } from '../../Sentry';
import { AddMediaDropdown } from '../Media/AddMediaDropdown';
import {
  PrimaryPhotoContainer,
  ProfilePhotoLabel,
  ProfilePhotoContainer,
  ProfileBirthday,
  StyledUserInfo,
  InfoRow,
  InfoHead,
  InfoDesc,
  ButtonContainer,
  StyledButton,
  StyledIcon,
} from './Styles';
import MySocials from '../Socials';

export type MediaMeatballProps = {
  mutationLoading?: boolean;
  openMediaBin: () => void;
  openMediaUploader: () => void;
  removeProfileMedia: () => void;
  uploadNewAllowed: boolean;
};

export const MediaMeatballMenu = ({
  mutationLoading,
  openMediaBin,
  openMediaUploader,
  uploadNewAllowed,
  removeProfileMedia,
}: MediaMeatballProps) => {
  const { t } = useTranslation();
  return (
    <div data-testid="media-meatball-menu">
      <MeatballMenu direction="right">
        <DropdownItem
          data-testid="media-meatball-menu-selectFromMediaLibrary"
          disabled={mutationLoading}
          onClick={() => openMediaBin()}
        >
          {t('common:media.upload.selectFromMediaLibrary')}
        </DropdownItem>
        <DropdownItem
          data-testid="media-meatball-menu-selectFromDevice"
          disabled={mutationLoading || !uploadNewAllowed}
          onClick={() => openMediaUploader()}
        >
          {t('common:media.upload.selectFromDevice')}
        </DropdownItem>
        <DropdownItem
          data-testid="media-meatball-menu-hideMedia"
          disabled={mutationLoading}
          onClick={() => removeProfileMedia()}
        >
          {t('common:profile.button.hideOnProfile')}
        </DropdownItem>
      </MeatballMenu>
    </div>
  );
};

const sectionName = 'basic-info';

const MediaWrapper: FC<{ 'data-testid'?: string }> = ({ 'data-testid': dataTestId, children }) => {
  return (
    <FlexDynamic
      data-qa-id={dataTestId}
      data-testid={dataTestId}
      w="full"
      smProps={{
        minW: '40',
        maxW: '50',
      }}
      grow={0}
      gap="2"
      direction="column"
      wrap="nowrap"
    >
      {children}
    </FlexDynamic>
  );
};

type MediaReelProps = {
  primaryMedia: gqlProfileMedia;
  sectionTitle: string;
  isPreview: boolean;
  openMediaBin: () => void;
  openMediaUploader: () => void;
  openMediaViewer: () => void;
  removeProfileMedia: () => void;
  uploadNewAllowed: boolean;
  dropdownToggleLabel: string;
  mediaType: MediaTypeCodeEnum;
};

const MediaReel = ({
  primaryMedia,
  sectionTitle,
  isPreview,
  openMediaBin,
  openMediaUploader,
  openMediaViewer,
  removeProfileMedia,
  uploadNewAllowed,
  dropdownToggleLabel,
  mediaType,
}: MediaReelProps) => {
  const mediaTypeQaId = mediaType?.toLowerCase();

  if (isPreview && !primaryMedia) {
    return null;
  }

  // if there is a primary media asset, render it
  // when not in preview mode there is a meatball menu that renders as well
  if (primaryMedia) {
    return (
      <MediaWrapper data-testid={`${mediaTypeQaId}-reel-wrap`}>
        <BodyText fontWeight="semibold">{sectionTitle}</BodyText>
        <BasicContainer
          className="cn_relative"
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${mediaTypeQaId}-wrapper`}
        >
          {!isPreview && (
            <BasicContainer className="cn_absolute" top={-0.25} right={-2}>
              <MediaMeatballMenu
                openMediaBin={openMediaBin}
                openMediaUploader={openMediaUploader}
                uploadNewAllowed={uploadNewAllowed}
                removeProfileMedia={removeProfileMedia}
              />
            </BasicContainer>
          )}
          <CloudinaryVideoAudio
            controls={mediaType !== MediaTypeCodeEnum.Video}
            fileKey={primaryMedia.media.fileKey}
            mediaStorageStatus={primaryMedia.media.mediaStorageStatus.code}
            thumbnailUrl={primaryMedia.media.thumbnailUrl}
            thumbnail={primaryMedia.media.thumbnail}
            guid={primaryMedia.media.guid}
            mediaType={primaryMedia.media.mediaType.code as MediaTypeCodeEnum}
            mediaStatus={primaryMedia.media.mediaStatus.code}
            type="xsm"
            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${mediaTypeQaId}`}
          />
          {mediaType === MediaTypeCodeEnum.Video && (
            <StyledIcon
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${mediaTypeQaId}-play-icon`}
              name="play-circle-filled"
              onClick={openMediaViewer}
            />
          )}
        </BasicContainer>
        <BasicContainer w="full">
          <BodyTextSmall
            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-${mediaTypeQaId}-name`}
            className="cn_word_break-break"
          >
            {primaryMedia.media.name}
          </BodyTextSmall>
        </BasicContainer>
      </MediaWrapper>
    );
  }

  // when not in preview mode and there is no primary media asset,
  // render the dropdown to select or upload a new media as the reel
  return (
    <MediaWrapper data-testid={`${mediaTypeQaId}-reel-wrap`}>
      <AddMediaDropdown
        openMediaBin={openMediaBin}
        openMediaUploader={openMediaUploader}
        toggleLabel={dropdownToggleLabel}
        uploadNewAllowed={uploadNewAllowed}
      />
    </MediaWrapper>
  );
};

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);

const getPrimaryMediaAsset = <T extends Partial<gqlProfileMedia | MediaAssetOnProfileFragment>>({
  profileMediaArr,
  mediaType,
}: {
  profileMediaArr: T[] | readonly T[];
  mediaType: MediaTypeCodeEnum;
}) => {
  return profileMediaArr?.find((pm) => pm?.media?.mediaType?.code === mediaType && pm?.isPrimary);
};

export const ProfileInfo = (): JSX.Element => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const billingPopup = useBillingPopup();
  const context = useProfileViewContext();
  const { appType, isPreview, isAdmin, onEditClick, fetcher, updater } = context;
  const [mediaUploaderContext, setMediaUploaderContext] = useState<{ mediaType: MediaTypeCodeEnum; maxFiles: number }>({
    mediaType: MediaTypeCodeEnum.Video,
    maxFiles: undefined,
  });
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const {
    isOpen: isMediaBinOpen,
    modalContext: mediaBinMediaType,
    close: closeMediaBin,
    open: openMediaBin,
  } = useModal<MediaTypeCodeEnum>();
  const [mediaBinSelectedId, setMediaBinSelectedId] = useState(null);

  const isReps = appType === AppType.Reps;
  const isTalent = appType === AppType.Talent;

  const { availableCreditsByType, entitlementsByType, profile, isUSCountry, audioMediaLibrary, videoMediaLibrary } =
    fetcher;

  const { profileId, blurb, workingLocations, mediaAssets, height, weight, dateOfBirth } = profile;

  const audioCreditsRemaining = availableCreditsByType?.[gqlMediaTypeCodeEnum.AUDIO];
  const uploadAudioAllowed =
    !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.AUDIO]) || audioCreditsRemaining > 0;
  const maxAudioUploads = !Number.isFinite(audioCreditsRemaining) ? undefined : audioCreditsRemaining;

  const videoCreditsRemaining = availableCreditsByType?.[gqlMediaTypeCodeEnum.VIDEO];
  const uploadVideoAllowed =
    !Number.isFinite(entitlementsByType[gqlMediaTypeCodeEnum.VIDEO]) || videoCreditsRemaining > 0;
  const maxVideoUploads = !Number.isFinite(videoCreditsRemaining) ? undefined : videoCreditsRemaining;

  const ageInYears = useMemo(() => getAgeInYears(profile?.dateOfBirth), [profile?.dateOfBirth]);
  const { primaryPhoto, primaryVideo, primaryAudio } = useMemo(() => {
    return {
      primaryPhoto: getPrimaryMediaAsset({ profileMediaArr: mediaAssets, mediaType: MediaTypeCodeEnum.Photo }),
      primaryVideo: getPrimaryMediaAsset({ profileMediaArr: mediaAssets, mediaType: MediaTypeCodeEnum.Video }),
      primaryAudio: getPrimaryMediaAsset({ profileMediaArr: mediaAssets, mediaType: MediaTypeCodeEnum.Audio }),
    };
  }, [mediaAssets]);

  const mediaBinMedia = useMemo(() => {
    // return the correct list of media assets based on the media type selected in the media bin
    // if an existing media is selected, it should be the first item in the list
    if (mediaBinMediaType === MediaTypeCodeEnum.Video) {
      return [
        ...videoMediaLibrary.filter((m) => m.mediaId === primaryVideo?.media?.mediaId),
        ...videoMediaLibrary.filter((m) => m.mediaId !== primaryVideo?.media?.mediaId),
      ];
    }

    if (mediaBinMediaType === MediaTypeCodeEnum.Audio) {
      return [
        ...audioMediaLibrary.filter((m) => m.mediaId === primaryAudio?.media?.mediaId),
        ...audioMediaLibrary.filter((m) => m.mediaId !== primaryAudio?.media?.mediaId),
      ];
    }

    return [];
  }, [mediaBinMediaType, audioMediaLibrary, videoMediaLibrary, primaryVideo, primaryAudio]);

  const setPrimaryMedia = useCallback(async () => {
    if (!mediaBinSelectedId) {
      closeMediaBin();

      return;
    }

    // if the user selects a media that is already attached to the profile we need to call updateProfileMedia
    // and tell it to make this media the primary for that type
    try {
      const mediaInProfileMedias = mediaAssets.find((mediaAsset) => mediaAsset.media.mediaId === mediaBinSelectedId);
      if (mediaInProfileMedias) {
        await updater.updateProfileMedia({ profileMediaId: mediaInProfileMedias.profileMediaId, isPrimary: true });
      } else {
        // if the media is not already attached to the profile we just make the call to attach it
        // as the primary directly
        const media = mediaBinMedia.find((m) => m.mediaId === mediaBinSelectedId);
        await updater.addProfileMedia({ mediaId: media.mediaId, isPrimary: true });
      }
    } catch (e) {
      captureExceptionEvent(e);
      showMessage({ color: 'danger', message: t('common:profile.mediaBank.setPrimaryFailed') });
    }
  }, [closeMediaBin, mediaAssets, mediaBinMedia, mediaBinSelectedId, showMessage, t, updater]);

  const handleMediaBinClick = (binMediaType: MediaTypeCodeEnum): void => {
    if (binMediaType === MediaTypeCodeEnum.Photo) {
      setMediaBinSelectedId(primaryPhoto?.media?.mediaId);
    }

    if (binMediaType === MediaTypeCodeEnum.Video) {
      setMediaBinSelectedId(primaryVideo?.media?.mediaId);
    }

    if (binMediaType === MediaTypeCodeEnum.Audio) {
      setMediaBinSelectedId(primaryAudio?.media?.mediaId);
    }
    openMediaBin(binMediaType);
  };

  const toggleCarouselModal = (isOpen: boolean): void => {
    setIsCarouselOpen(isOpen);
  };

  const getAgeText = (): string => {
    let age = 0;
    let ageSuffix = '';

    if (dateOfBirth) {
      const monthsCount = moment().diff(dateOfBirth, 'months');

      if (monthsCount < 1) {
        // age in days
        const daysCount = moment().diff(dateOfBirth, 'days');

        age = daysCount;
        ageSuffix = daysCount > 0 ? t('common:suffix.days') : '';
      } else if (monthsCount >= 1 && monthsCount < 24) {
        // age in months
        age = monthsCount;
        ageSuffix = t('common:suffix.months');
      } else {
        // age in years
        age = Math.floor(monthsCount / 12);
      }
    }

    if (age === 0) return '';

    return `${age}${ageSuffix}`;
  };

  const edit = t('common:button.edit');

  const blurbButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-about-edit`}
      onClick={(): void => {
        onEditClick('about');
      }}
    >
      {edit}
    </AnchorButton>
  ) : (
    <DuoLink data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-about-edit`} href={`/profile/${profileId}/about`}>
      {edit}
    </DuoLink>
  );
  const commonBlurbButton = isAdmin ? (
    <StyledButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-about-add`}
      onClick={(): void => {
        onEditClick('about');
      }}
      outlined="true"
    >
      {t('common:form.title.about')}{' '}
    </StyledButton>
  ) : (
    <DuoLink data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-about-add`} href={`/profile/${profileId}/about`}>
      <StyledButton outlined="true">{t('common:form.title.about')} </StyledButton>
    </DuoLink>
  );
  const workingLocationsButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-working-locations-edit`}
      onClick={(): void => {
        onEditClick('working-locations');
      }}
    >
      {edit}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-working-locations-edit`}
      href={`/profile/${profileId}/working-locations`}
    >
      {edit}
    </DuoLink>
  );
  const commonWorkingLocationsButton = isAdmin ? (
    <StyledButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-working-locations-add`}
      onClick={(): void => {
        onEditClick('working-locations');
      }}
      outlined="true"
    >
      {t('common:profile.button.addPrimaryWorkLocation')}
    </StyledButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-working-locations-add`}
      href={`/profile/${profileId}/working-locations`}
    >
      <StyledButton outlined="true">{t('common:profile.button.addPrimaryWorkLocation')} </StyledButton>
    </DuoLink>
  );
  const heightButton = isAdmin ? (
    <AnchorButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight-edit`}
      onClick={(): void => {
        onEditClick('height-weight');
      }}
    >
      {edit}
    </AnchorButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight-edit`}
      href={`/profile/${profileId}/height-weight`}
    >
      {edit}
    </DuoLink>
  );
  const commonHeightButton = isAdmin ? (
    <StyledButton
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight-add`}
      onClick={(): void => {
        onEditClick('height-weight');
      }}
      outlined="true"
    >
      {t('common:profile.button.addHeightAndWeight')}
    </StyledButton>
  ) : (
    <DuoLink
      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight-add`}
      href={`/profile/${profileId}/height-weight`}
    >
      <StyledButton outlined="true">{t('common:profile.button.addHeightAndWeight')} </StyledButton>
    </DuoLink>
  );
  const infoDescUSCountry = isUSCountry ? (
    <InfoDesc data-testid="ht-wt-text" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight`}>
      {height &&
        height.IMPERIAL_FEET_INCHES.replace("'", ` ${t('common:measurement.unit.ft')} `).replace(
          '"',
          ` ${t('common:measurement.unit.in')} `
        )}
      {height && weight && ' / '}
      {weight && `${weight.IMPERIAL_LBS} ${t('common:measurement.unit.lbs')}`}
    </InfoDesc>
  ) : (
    <InfoDesc data-testid="ht-wt-text" data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-height-weight`}>
      {height && `${height.METRIC_CENTIMETERS} ${t('common:measurement.unit.cm')}`}
      {height && weight && ' / '}
      {weight && `${weight.kgRounded} ${t('common:measurement.unit.kg')}`}
    </InfoDesc>
  );
  const ageButton = !isPreview && (
    <>
      {isAdmin ? (
        <AnchorButton
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-birthday-edit`}
          onClick={(): void => {
            onEditClick('birthday');
          }}
        >
          {edit}
        </AnchorButton>
      ) : (
        <DuoLink
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-birthday-edit`}
          href={`/profile/${profileId}/birthday`}
        >
          {edit}
        </DuoLink>
      )}
    </>
  );

  const handleMediaUpload: MediaUploaderProps['onCloseUploader'] = useCallback(
    async (succeededUploads) => {
      try {
        let shouldAttach = false;

        const uploadedMedia = succeededUploads?.[0];
        if (isReps && uploadedMedia && uploadedMedia?.isLocked) {
          // show alert message and bail
          // this should only be possible if someone in another
          // session uploaded and consumed a credit since this session started
          // b/c the upload button is disabled when the user has no credits
          showMessage({ color: 'danger', message: t('common:profile.mediaBank.attachMediaFailed') });
          await updater.refetch({ refetchProfile: true, refetchMedia: true });
        }

        if (isTalent && uploadedMedia && uploadedMedia?.isLocked) {
          const { getPurchaseResult } = billingPopup.show({
            location: PaywallLocation.PROFILE_MEDIA,
            mediaIds: [uploadedMedia?.mediaId],
          });
          const paywallResult = await getPurchaseResult();
          if (paywallResult?.type === PurchaseResultType.Success) {
            shouldAttach = true;
          }
        }

        if (uploadedMedia && !uploadedMedia?.isLocked) {
          shouldAttach = true;
        }

        if (shouldAttach) {
          await updater.addProfileMedia({
            mediaId: uploadedMedia.mediaId,
            isPrimary: true,
          });
        }
      } catch (e) {
        captureExceptionEvent(e);
        showMessage({
          color: 'danger',
          message: t('common:profile.mediaBank.attachMediaFailed'),
        });
      }
    },
    [updater, billingPopup, showMessage, t, isReps, isTalent]
  );

  return (
    <MediaUploader
      artistId={Number(fetcher?.artistId)}
      availableCreditsByType={availableCreditsByType}
      entitlementsByType={entitlementsByType}
      mediaType={mediaUploaderContext?.mediaType}
      openModalButtonText={t('common:profile.button.uploadNew')}
      onCloseUploader={handleMediaUpload}
      maxFiles={1}
      renderChildren={({ toggleModal: toggleMediaUploader }) => (
        <FlexDynamic
          justifyContent="flex-start"
          direction="column"
          smProps={{ direction: 'row' }}
          data-testid="profile-basic-info-wrapper"
          wrap="nowrap"
          w="full"
        >
          <PrimaryPhotoContainer data-testid="primary-photo-wrapper">
            <ProfilePhotoLabel>
              {isPreview ? t('common:profile.nav.basicInfo') : t('common:mediaType.photoItem')}
            </ProfilePhotoLabel>
            <ProfilePhotoContainer>
              <CloudinaryPhoto
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-photo`}
                type="lg"
                guid={primaryPhoto?.media?.guid}
                fileKey={primaryPhoto?.media?.fileKey}
                transformation={primaryPhoto?.media?.transformation}
                isPhotoLocked={!isPreview && !isReps && primaryPhoto?.media?.isLocked}
                mediaStorageStatus={primaryPhoto?.media.mediaStorageStatus}
              />
              {profile?.dateOfBirth && ageInYears < 18 && (
                <ProfileBirthday data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-date-of-birth`}>
                  <Icon name="age" themeColor="inverted" />
                  {getAgeText()}
                </ProfileBirthday>
              )}
            </ProfilePhotoContainer>
          </PrimaryPhotoContainer>
          <FlexDynamic direction="column" wrap="nowrap" w="full" alignItems="normal">
            {blurb ? (
              <StyledUserInfo data-testid="blurb-wrapper">
                <InfoRow>
                  <InfoHead>{t('common:profile.info.about')}</InfoHead>
                  {!isPreview && blurbButton}
                </InfoRow>
                <InfoDesc data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-about`}>{blurb}</InfoDesc>
              </StyledUserInfo>
            ) : (
              !isPreview && <ButtonContainer>{commonBlurbButton}</ButtonContainer>
            )}
            {workingLocations &&
            workingLocations.length > 0 &&
            _sortBy(workingLocations, (o) => o.sortOrder)[0].name ? (
              <StyledUserInfo data-testid="primary-working-location">
                <InfoRow>
                  <InfoHead>{t('common:profile.info.primaryWorkLocation')}</InfoHead>
                  {!isPreview && workingLocationsButton}
                </InfoRow>
                <InfoDesc data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-primary-working-location`}>
                  {_sortBy(workingLocations, (o) => o.sortOrder)[0].name}
                </InfoDesc>
              </StyledUserInfo>
            ) : (
              !isPreview && <ButtonContainer>{commonWorkingLocationsButton}</ButtonContainer>
            )}
            {(height && height.IMPERIAL_FEET_INCHES) || (weight && weight.IMPERIAL_LBS) ? (
              <StyledUserInfo data-testid="height-and-weight">
                <InfoRow>
                  <InfoHead>{t('common:profile.info.heightAndWeight')}</InfoHead>
                  {!isPreview && heightButton}
                </InfoRow>
                {infoDescUSCountry}
              </StyledUserInfo>
            ) : (
              !isPreview && <ButtonContainer>{commonHeightButton}</ButtonContainer>
            )}
            {(!isPreview || ageInYears < 18) && (
              <StyledUserInfo data-testid="profile-age-wrap">
                <InfoRow>
                  <InfoHead>{t('common:profile.info.age')}</InfoHead>
                  {ageButton}
                </InfoRow>
                <InfoDesc data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-birthday`}>{getAgeText()}</InfoDesc>
              </StyledUserInfo>
            )}
            <CreditMedia
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-credits`}
              profile={profile}
              isAdmin={isAdmin}
              onEditClick={onEditClick}
              isPreview={isPreview}
              appType={appType}
            />
            <MySocials isPreview={isPreview} />
            <FlexDynamic
              w="full"
              direction="column"
              justifyContent="flex-start"
              gap="12"
              smProps={{ direction: 'row', alignItems: 'flex-start' }}
              data-testid="basic-info-media-wrap"
              mt="4"
            >
              <MediaReel
                mediaType={MediaTypeCodeEnum.Video}
                primaryMedia={primaryVideo}
                sectionTitle={t('common:profile.info.videoReel')}
                dropdownToggleLabel={t('common:profile.button.addVideoReel')}
                openMediaBin={() => handleMediaBinClick(MediaTypeCodeEnum.Video)}
                openMediaUploader={() => {
                  setMediaUploaderContext({ mediaType: MediaTypeCodeEnum.Video, maxFiles: maxVideoUploads });
                  toggleMediaUploader();
                }}
                openMediaViewer={() => toggleCarouselModal(true)}
                isPreview={isPreview}
                uploadNewAllowed={isTalent || uploadVideoAllowed}
                removeProfileMedia={() => updater.hideProfileMedia({ mediaId: primaryVideo?.media?.mediaId })}
              />
              <MediaReel
                mediaType={MediaTypeCodeEnum.Audio}
                primaryMedia={primaryAudio}
                sectionTitle={t('common:profile.info.audioReel')}
                dropdownToggleLabel={t('common:profile.button.addAudioReel')}
                openMediaBin={() => handleMediaBinClick(MediaTypeCodeEnum.Audio)}
                openMediaUploader={() => {
                  setMediaUploaderContext({ mediaType: MediaTypeCodeEnum.Audio, maxFiles: maxAudioUploads });
                  toggleMediaUploader();
                }}
                openMediaViewer={() => undefined}
                isPreview={isPreview}
                uploadNewAllowed={isTalent || uploadAudioAllowed}
                removeProfileMedia={() => updater.hideProfileMedia({ mediaId: primaryAudio?.media?.mediaId })}
              />
            </FlexDynamic>
          </FlexDynamic>
          {isMediaBinOpen && (
            <Modal
              isOpen
              title={isReps ? t('common:profile.talentMedia') : t('common:profile.mediaBank')}
              handleToggleClick={() => closeMediaBin()}
              submitLabel={mediaBinSelectedId ? t('common:button.save') : t('common:button.cancel')}
              handleSubmitClick={() => setPrimaryMedia()}
              data-testid="media-bin-modal"
            >
              <MediaBin
                data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}
                selectedMediaIds={mediaBinSelectedId ? [mediaBinSelectedId] : []}
                mediaTypes={[mediaBinMediaType]}
                onChange={({ mediaId }) => setMediaBinSelectedId(mediaId)}
                medias={mediaBinMedia}
                isSingleSelection
              />
            </Modal>
          )}
          {isCarouselOpen && (
            <CloudinaryMediaViewer
              isSmall
              stageName=""
              mediaList={[primaryVideo]}
              handleCloseModal={() => toggleCarouselModal(false)}
            />
          )}
        </FlexDynamic>
      )}
    />
  );
};
